import { Bot, FolderTree, Layers3, Sparkles, TerminalSquare, Workflow } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type HeroNodeId = "chat" | "knowledge" | "files" | "apps" | "blueprints" | "terminal";

type NodeDetail = {
  label: string;
  eyebrow: string;
  chip: string;
  title: string;
  description: string;
  highlights: string[];
  metrics: { label: string; value: string }[];
  image: string;
  imageAlt: string;
};

const nodeIcons = {
  chat: Bot,
  knowledge: Sparkles,
  files: FolderTree,
  apps: Layers3,
  blueprints: Workflow,
  terminal: TerminalSquare,
} satisfies Record<HeroNodeId, typeof Bot>;

const nodeDetails: Record<HeroNodeId, NodeDetail> = {
  chat: {
    label: "Chat",
    eyebrow: "Chat",
    chip: "Conversational AI",
    title: "Command the whole system through chat",
    description:
      "Chat is the front door into MapleOS — a live conversational layer that can reason across context, retrieve knowledge, invoke tools, and hand work off to the rest of the system in a single flow.",
    highlights: [
      "Persistent conversations with execution-ready context",
      "Model-assisted planning that hands work into apps, files, and blueprints",
      "Fast multimodal interaction for asking, refining, and shipping in one place",
    ],
    metrics: [
      { label: "Mode", value: "Interactive" },
      { label: "Latency", value: "Live" },
      { label: "Scope", value: "System-wide" },
    ],
    image: "/images/mapleos/MapleOS-Chat.png",
    imageAlt: "MapleOS chat screenshot",
  },
  knowledge: {
    label: "Knowledge",
    eyebrow: "memory + context",
    chip: "Structured memory layer",
    title: "Turn raw information into working memory",
    description:
      "Knowledge gives MapleOS durable context: documents, notes, entities, prior decisions, and operational memory that can be recalled exactly when the system needs it.",
    highlights: [
      "Long-term memory anchored to projects, teams, and tasks",
      "Searchable context that can be surfaced inside chat, blueprints, and apps",
      "Signal-rich retrieval instead of disconnected files and tabs",
    ],
    metrics: [
      { label: "Memory", value: "Persistent" },
      { label: "Search", value: "Semantic" },
      { label: "Access", value: "Cross-node" },
    ],
    image: "/images/mapleos/apps/notepad.png",
    imageAlt: "MapleOS knowledge and notes workspace screenshot",
  },
  files: {
    label: "Files",
    eyebrow: "workspace",
    chip: "Operational file system",
    title: "Keep every asset inside the same live workspace",
    description:
      "Files is more than storage — it is the working surface for prompts, documents, artifacts, code, exports, and generated assets that the rest of MapleOS can immediately understand and use.",
    highlights: [
      "Project-aware organization for documents, code, and generated output",
      "Instant handoff from chat and blueprints into editable assets",
      "A workspace graph that keeps the system grounded in real files",
    ],
    metrics: [
      { label: "Artifacts", value: "Unified" },
      { label: "State", value: "Tracked" },
      { label: "Flow", value: "Editable" },
    ],
    image: "/images/mapleos/MapleOS-Files.png",
    imageAlt: "MapleOS desktop workspace screenshot",
  },
  apps: {
    label: "Apps",
    eyebrow: "modular runtime",
    chip: "Execution surfaces",
    title: "Build apps without leaving the system",
    description:
      "Apps are the specialized interfaces of MapleOS — focused execution surfaces for writing, analysis, creation, and operations, all connected back to the same cognitive core.",
    highlights: [
      "Native app experiences for work that needs dedicated interfaces",
      "Shared context with chat, files, and blueprints instead of siloed tools",
      "A modular runtime that can expand as MapleOS grows",
    ],
    metrics: [
      { label: "Runtime", value: "Modular" },
      { label: "Context", value: "Shared" },
      { label: "Output", value: "Actionable" },
    ],
    image: "/images/mapleos/MapleOS-Apps.png",
    imageAlt: "MapleOS applications and productivity workspace screenshot",
  },
  blueprints: {
    label: "Blueprints",
    eyebrow: "blueprint://launch",
    chip: "Automation orchestration",
    title: "Design repeatable intelligence as visual workflows",
    description:
      "Blueprints turns complex reasoning and automation into reusable flows. It is where agents, tools, data, and control logic come together as a system you can inspect, refine, and run again.",
    highlights: [
      "Visual orchestration for multi-step AI workflows",
      "Composable nodes for tools, memory, logic, and execution",
      "A bridge from one-off prompting to repeatable operational systems",
    ],
    metrics: [
      { label: "Flows", value: "Reusable" },
      { label: "Nodes", value: "Composable" },
      { label: "Runs", value: "Inspectable" },
    ],
    image: "/images/mapleos/apps/blueprint-studio.png",
    imageAlt: "MapleOS blueprint studio workflow screenshot",
  },
  terminal: {
    label: "Terminal",
    eyebrow: "shell://runtime",
    chip: "Direct execution channel",
    title: "Drop to the system layer whenever you need precision",
    description:
      "Terminal is the direct line into execution — ideal for commands, diagnostics, scripting, and developer-grade control, all while staying connected to the same MapleOS context model.",
    highlights: [
      "Command execution with logs, output, and runtime visibility",
      "Ideal for power users, automation hooks, and debugging",
      "A precise fallback whenever graphical interfaces are not enough",
    ],
    metrics: [
      { label: "Access", value: "Low-level" },
      { label: "Use case", value: "Power user" },
      { label: "Feedback", value: "Real-time" },
    ],
    image: "/images/mapleos/MapleOS-Terminal.png",
    imageAlt: "MapleOS terminal screenshot",
  },
};

type HeroNodeDetailsDialogProps = {
  nodeId: HeroNodeId | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function HeroNodeDetailsDialog({ nodeId, open, onOpenChange }: HeroNodeDetailsDialogProps) {
  const detail = nodeId ? nodeDetails[nodeId] : null;

  if (!detail) {
    return <Dialog open={open} onOpenChange={onOpenChange} />;
  }

  const Icon = nodeIcons[nodeId];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] max-w-6xl overflow-hidden border-white/10 bg-[#04070c] p-0 text-white shadow-[0_45px_140px_rgba(0,0,0,0.72)] sm:rounded-[1.75rem]">
        <div className="relative max-h-[88vh] overflow-y-auto">
          <div className="pointer-events-none absolute inset-x-[18%] top-0 h-48 rounded-full bg-primary/14 blur-3xl" />
          <div className="pointer-events-none absolute -right-12 top-24 h-52 w-52 rounded-full bg-emerald-400/10 blur-3xl" />

          <div className="relative grid gap-8 p-5 sm:p-7 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:p-8">
            <div className="space-y-4">
              <div className="rounded-[1.6rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,24,0.98),rgba(7,10,16,0.98))] p-3 shadow-[0_28px_90px_rgba(0,0,0,0.45)]">
                <div className="mb-3 flex items-center justify-between rounded-[1rem] border border-white/6 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-white/45">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    {detail.label} node
                  </div>
                  <span>{detail.eyebrow}</span>
                </div>
                <div className="overflow-hidden rounded-[1.2rem] border border-white/10 bg-black/60">
                  <img src={detail.image} alt={detail.imageAlt} className="h-full w-full object-cover" loading="lazy" />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {detail.metrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-[1.2rem] border border-white/8 bg-white/[0.035] px-4 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.24)]"
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">{metric.label}</div>
                    <div className="mt-2 text-sm font-medium text-white/88">{metric.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-between gap-6">
              <DialogHeader className="space-y-4 text-left">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-primary/85">
                    <Icon className="h-3.5 w-3.5" />
                    {detail.eyebrow}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/55">
                    {detail.chip}
                  </span>
                </div>
                <DialogTitle className="max-w-lg text-3xl font-semibold leading-tight text-white sm:text-[2.4rem]">
                  {detail.title}
                </DialogTitle>
                <DialogDescription className="max-w-xl text-base leading-7 text-white/64">
                  {detail.description}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                {detail.highlights.map((highlight) => (
                  <div
                    key={highlight}
                    className="flex items-start gap-3 rounded-[1.2rem] border border-white/8 bg-white/[0.035] px-4 py-3.5"
                  >
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_18px_rgba(132,255,76,0.65)]" />
                    <p className="text-sm leading-6 text-white/72">{highlight}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-[1.4rem] border border-primary/15 bg-[linear-gradient(180deg,rgba(132,255,76,0.08),rgba(132,255,76,0.03))] px-5 py-4 text-sm leading-6 text-white/70 shadow-[0_18px_50px_rgba(132,255,76,0.08)]">
                <span className="block text-[11px] uppercase tracking-[0.28em] text-primary/75">Why it matters</span>
                <p className="mt-2">
                  Each node is a distinct surface in the MapleOS graph, but it stays connected to the same cognitive core — which means context, intent, and output can move cleanly across the entire system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
